import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Tag, Percent, IndianRupee, Zap, Ticket } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, EmptyState, LoadingBlock, StatusBadge } from "../../components/app/ui";
import { useMyRestaurant } from "./RestaurantOrders";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import { restaurantApi, inr, errorMessage } from "../../lib/api";

const EMPTY = { description: "", discount_type: "percent", value: "", code: "", auto_apply: false, min_order: "", max_discount: "", max_uses: "", max_uses_per_user: 10, expires_at: "", active: true };

function DiscountDialog({ open, onOpenChange, discount, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(discount ? {
      description: discount.description || "", discount_type: discount.discount_type, value: String(discount.value),
      code: discount.code?.startsWith("AUTO-") ? "" : discount.code, auto_apply: discount.auto_apply,
      min_order: discount.min_order || "", max_discount: discount.max_discount || "", max_uses: discount.max_uses || "",
      max_uses_per_user: discount.max_uses_per_user || 10, expires_at: discount.expires_at ? discount.expires_at.slice(0, 10) : "", active: discount.active,
    } : EMPTY);
  }, [open, discount]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Add a short description");
    if (!form.value || Number(form.value) <= 0) return toast.error("Enter a discount value");
    if (!form.auto_apply && !form.code.trim()) return toast.error("Enter a code or enable auto-apply");
    setBusy(true);
    try {
      const body = {
        description: form.description.trim(), discount_type: form.discount_type, value: Number(form.value),
        code: form.code.trim() ? form.code.trim().toUpperCase() : null, auto_apply: form.auto_apply,
        min_order: Number(form.min_order) || 0, max_discount: form.max_discount ? Number(form.max_discount) : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null, max_uses_per_user: Number(form.max_uses_per_user) || 10,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null, active: form.active,
      };
      const saved = discount ? await restaurantApi.updateDiscount(discount.promo_id, body) : await restaurantApi.createDiscount(body);
      onSaved(saved, !!discount);
      toast.success(discount ? "Discount updated" : "Discount created");
      onOpenChange(false);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="discount-dialog">
        <DialogHeader><DialogTitle>{discount ? "Edit discount" : "New discount"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={set("description")} placeholder="e.g. 20% off orders above ₹299" data-testid="discount-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Type</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm((f) => ({ ...f, discount_type: "percent" }))} className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2 text-sm font-medium ${form.discount_type === "percent" ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft"}`} data-testid="discount-type-percent"><Percent className="h-4 w-4" /> %</button>
                <button type="button" onClick={() => setForm((f) => ({ ...f, discount_type: "flat" }))} className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2 text-sm font-medium ${form.discount_type === "flat" ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft"}`} data-testid="discount-type-flat"><IndianRupee className="h-4 w-4" /> Flat</button>
              </div>
            </div>
            <div className="space-y-2"><Label>{form.discount_type === "percent" ? "Percent off" : "Amount off (₹)"}</Label><Input type="number" min="1" value={form.value} onChange={set("value")} data-testid="discount-value" /></div>
          </div>
          <label className="flex items-center justify-between rounded-xl bg-surface-100 px-4 py-3">
            <div><p className="flex items-center gap-1.5 text-sm font-medium text-ink"><Zap className="h-4 w-4 text-brand" /> Auto-apply</p><p className="text-xs text-ink-soft">Applied automatically — no code needed by customers.</p></div>
            <Switch checked={form.auto_apply} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_apply: v }))} data-testid="discount-auto" />
          </label>
          {!form.auto_apply && <div className="space-y-2"><Label>Discount code</Label><Input value={form.code} onChange={set("code")} placeholder="SAVE20" className="uppercase" data-testid="discount-code" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Min order (₹)</Label><Input type="number" min="0" value={form.min_order} onChange={set("min_order")} data-testid="discount-min" /></div>
            {form.discount_type === "percent" && <div className="space-y-2"><Label>Max discount (₹)</Label><Input type="number" min="0" value={form.max_discount} onChange={set("max_discount")} placeholder="optional" data-testid="discount-maxoff" /></div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Total uses</Label><Input type="number" min="0" value={form.max_uses} onChange={set("max_uses")} placeholder="Unlimited" data-testid="discount-maxuses" /></div>
            <div className="space-y-2"><Label>Expires on</Label><Input type="date" value={form.expires_at} onChange={set("expires_at")} data-testid="discount-expiry" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="discount-active" /> Active</label>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="discount-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {discount ? "Save" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Discounts() {
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => restaurantApi.discounts().then(setList).catch(() => setList([]));
  useEffect(() => { if (restaurant) load(); }, [restaurant]);

  const onSaved = (saved, wasEdit) => {
    setList((l) => {
      if (!l) return l;
      return wasEdit ? l.map((x) => (x.promo_id === saved.promo_id ? saved : x)) : [saved, ...l];
    });
    load();
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.description}"?`)) return;
    try { await restaurantApi.deleteDiscount(d.promo_id); setList((l) => l.filter((x) => x.promo_id !== d.promo_id)); toast.success("Discount deleted"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (restaurant === undefined) return <><PageHeader title="Discounts" /><LoadingBlock rows={3} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" description="Create your restaurant profile first." /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;

  return (
    <>
      <PageHeader title="Discounts & offers" description="Run percentage or flat discounts — auto-apply them or share a code." actions={<Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="discount-new"><Plus className="mr-2 h-4 w-4" /> New discount</Button>} />
      {list === null && <LoadingBlock rows={3} />}
      {list && list.length === 0 && <EmptyState icon={Tag} title="No discounts yet" text="Create your first offer with the New discount button to attract more orders." />}
      {list && list.length > 0 && (
        <div className="grid gap-4" data-testid="discounts-list">
          {list.map((d) => (
            <div key={d.promo_id} className="kt-card flex flex-wrap items-center gap-4 p-5" data-testid={`discount-${d.promo_id}`}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand">{d.discount_type === "percent" ? <Percent className="h-5 w-5" /> : <IndianRupee className="h-5 w-5" />}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-ink">{d.discount_type === "percent" ? `${d.value}% off` : `${inr(d.value)} off`}</p>
                  {d.auto_apply ? <span className="inline-flex items-center gap-1 rounded-full bg-graphite-50 px-2 py-0.5 text-[11px] font-semibold text-graphite" data-testid={`auto-${d.promo_id}`}><Zap className="h-3 w-3" /> Auto</span> : <span className="inline-flex items-center gap-1 rounded-full bg-surface-200 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-ink" data-testid={`code-${d.promo_id}`}><Ticket className="h-3 w-3" /> {d.code}</span>}
                  <StatusBadge status={d.active ? "active" : "paused"} />
                </div>
                <p className="mt-0.5 truncate text-sm text-ink-soft">{d.description}</p>
                <p className="mt-1 text-xs text-ink-muted">{d.min_order > 0 ? `Min ${inr(d.min_order)} · ` : ""}{d.max_discount ? `Up to ${inr(d.max_discount)} · ` : ""}{d.uses} used{d.max_uses ? ` / ${d.max_uses}` : ""}{d.expires_at ? ` · ends ${d.expires_at.slice(0, 10)}` : ""}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(d); setOpen(true); }} className="rounded-lg p-2 text-ink-muted hover:bg-surface-100 hover:text-ink" aria-label="Edit" data-testid={`discount-edit-${d.promo_id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(d)} className="rounded-lg p-2 text-ink-muted hover:bg-red-50 hover:text-red-700" aria-label="Delete" data-testid={`discount-delete-${d.promo_id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <DiscountDialog open={open} onOpenChange={setOpen} discount={editing} onSaved={onSaved} />
    </>
  );
}
